/** The Upcoming page: listings, chips, RSVP and edits. Filtering is the server's (upcoming_logic.filter_upcoming). */
import type { Reading } from "./flyer";
import { monthLabel } from "./format";
import { getJson, qs, send } from "./request";
import type { Rsvp } from "./types";

export interface UpcomingRow {
  id: number;
  event_name: string;
  date: string;
  venue: string;
  matched_artist: string | null;
  price: string | null;
  url: string | null;
  discovered_at: string | null;
  rsvp: Rsvp | "hidden" | "deleted";
  image_url: string | null;
  event_label: string | null;
  start_time: string | null;
  end_time: string | null;
  time: string | null;
  calendar_link: string;
  /** The listing's bill, split server-side by the Add dialog's rule. */
  bands: string[];
}
export interface UpcomingDto {
  total: number;
  shows: UpcomingRow[];
}

export type Chip = "all" | "going" | "maybe" | "new";
export const CHIPS: { value: Chip; label: string }[] = [
  { value: "all", label: "All" },
  { value: "going", label: "Going" },
  { value: "maybe", label: "Maybe" },
  { value: "new", label: "New" },
];
export const RSVPS: { value: Exclude<Rsvp, null>; label: string }[] = [
  { value: "yes", label: "Going" },
  { value: "maybe", label: "Maybe" },
  { value: "no", label: "No" },
];

/** A chip as the server's filter params. New = no RSVP yet. */
export function upcomingQuery(chip: Chip, hideNotGoing: boolean): string {
  return qs({
    rsvp: chip === "going" ? "Going" : chip === "maybe" ? "Maybe" : null,
    only_new: chip === "new",
    // hide_not_going defaults to true on the server, so only "off" is sent.
    hide_not_going: hideNotGoing ? null : "false",
  });
}

export const fetchUpcoming = (chip: Chip, hideNotGoing: boolean) =>
  getJson<UpcomingDto>(`/api/upcoming${upcomingQuery(chip, hideNotGoing)}`, "Could not load upcoming shows");
export const fetchLinked = (id: number) => getJson<UpcomingDto>(`/api/upcoming?show=${id}`, "Could not load that show");

export const setRsvp = (id: number, rsvp: Rsvp) => send(`/api/upcoming/${id}/rsvp`, "PUT", { rsvp }, "Could not save the RSVP");
/** Reconciles the synced calendar on every load (the server caps it). */
export const reconcileCalendar = () => send("/api/upcoming/calendar/reconcile", "POST", undefined, "Could not sync the calendar");
export const hideUpcoming = (id: number) => send(`/api/upcoming/${id}`, "DELETE", undefined, "Could not remove it");

export interface UpcomingEdit {
  event_name: string;
  date: string;
  venue: string;
  event_label: string;
  start_time: string;
  end_time: string;
}
const editBody = (e: UpcomingEdit) => ({
  event_name: e.event_name.trim(),
  date: e.date,
  venue: e.venue.trim(),
  event_label: e.event_label.trim() || null,
  start_time: e.start_time.trim() || null,
  end_time: e.end_time.trim() || null,
});
export const editUpcoming = (id: number, e: UpcomingEdit) => send(`/api/upcoming/${id}`, "PUT", editBody(e), "Could not save it");
/** Add an upcoming show's fields beyond the ones Edit has. */
export interface UpcomingExtra {
  url: string;
  price: string;
  rsvp: Rsvp;
}
export const addUpcoming = (e: UpcomingEdit & UpcomingExtra & { image_url: string | null }) =>
  send<{ id: number }>("/api/upcoming", "POST",
    { ...editBody(e), url: e.url.trim() || null, price: e.price.trim() || null, rsvp: e.rsvp, image_url: e.image_url }, "Could not add it");
export const setUpcomingFlyer = (id: number, image_url: string | null) =>
  send(`/api/upcoming/${id}/image`, "PUT", { image_url }, "Could not save the flyer");

/** A listing's bill as typed ("A, B, C"), for the flyer's name and its image search. */
export const billOf = (eventName: string) => eventName.split(",").map((b) => b.trim()).filter(Boolean);

/** What a flyer said, as patches for the Add sheet's fields; a field it did not give is left out. */
export function listingReading(r: Reading): { e: Partial<UpcomingEdit>; extra: Partial<UpcomingExtra> } {
  const e: Partial<UpcomingEdit> = {};
  if (r.draft.bands.length) e.event_name = r.draft.bands.join(", ");
  if (r.draft.date) e.date = r.draft.date;
  const venue = r.draft.venue_name.trim();
  if (venue) e.venue = venue;
  if (r.draft.event_name) e.event_label = r.draft.event_name;
  const extra: Partial<UpcomingExtra> = {};
  if (r.listing.price) extra.price = r.listing.price;
  if (r.listing.ticket_url) extra.url = r.listing.ticket_url;
  return { e, extra };
}

export const editFrom = (u: UpcomingRow): UpcomingEdit => ({
  event_name: u.event_name,
  date: u.date,
  venue: u.venue,
  event_label: u.event_label ?? "",
  start_time: u.start_time ?? "",
  end_time: u.end_time ?? "",
});

export interface MonthGroup {
  month: string;
  shows: UpcomingRow[];
}
export function byMonth(rows: UpcomingRow[]): MonthGroup[] {
  const out: MonthGroup[] = [];
  for (const r of rows) {
    const mo = monthLabel(r.date);
    const last = out[out.length - 1];
    if (last && last.month === mo) last.shows.push(r);
    else out.push({ month: mo, shows: [r] });
  }
  return out;
}

/** The "new" badge: no RSVP yet and discovered in the last week. */
export function isNew(u: UpcomingRow, today: string): boolean {
  if (u.rsvp !== null || !u.discovered_at) return false;
  const found = new Date(`${u.discovered_at.slice(0, 10)}T00:00:00`);
  const now = new Date(`${today}T00:00:00`);
  return (now.getTime() - found.getTime()) / 86_400_000 <= 7;
}

/** Going or Maybe keeps a synced calendar event (gcal_sync.event_should_exist). */
export const onCalendar = (u: UpcomingRow) => u.rsvp === "yes" || u.rsvp === "maybe";
